import { useCallback, useEffect, useRef, useState } from 'react'
import { useApp } from '../../context/AppContext.jsx'
import Icon from '../../components/icons.jsx'
import { pjGetBoard, pjCreateStage, pjUpdateStage, pjDeleteStage } from '../../db/api.js'
import { stageOrderUpdates } from './projectStageOrder.js'
import './project-stages-modal.css'

export default function ProjectStagesModal({ project, onClose, onChanged }) {
  const { confirm } = useApp()
  const [stages, setStages] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(null)
  const [name, setName] = useState('')
  const [newName, setNewName] = useState('')
  const locked = useRef(false)
  const mounted = useRef(false)
  const root = useRef(null)
  const load = useCallback(async () => {
    const board = await pjGetBoard(project.id)
    if (mounted.current) setStages(board.columns.filter(stage => stage.id))
  }, [project.id])

  useEffect(() => {
    mounted.current = true
    const previousFocus = document.activeElement
    root.current?.focus()
    load().catch(e => { if (mounted.current) setError(e.message) })
    return () => { mounted.current = false; previousFocus?.focus() }
  }, [load])

  const close = () => { if (!locked.current) onClose() }
  const mutate = async operation => {
    if (locked.current) return
    locked.current = true
    setBusy(true); setError('')
    try {
      await operation()
      if (!mounted.current) return
      setEditing(null); setNewName('')
      onChanged?.()
      await load()
    } catch (e) {
      if (mounted.current) {
        setError(e.message || 'Não foi possível salvar as etapas.')
        // Reordering uses existing individual updates; reconcile partial success.
        onChanged?.()
        try { await load() } catch { setStages(null) }
      }
    } finally {
      locked.current = false
      if (mounted.current) setBusy(false)
    }
  }
  const remove = async stage => {
    if (locked.current) return
    const count = stage.tasks?.length || 0
    const accepted = await confirm({ title: 'Excluir etapa',
      message: count ? `Excluir a etapa "${stage.nome}"? Suas ${count} tarefa(s) serão mantidas e aparecerão em "Sem coluna".` : `Excluir a etapa "${stage.nome}"?`,
      confirmText: 'Excluir etapa', danger: true })
    if (accepted && mounted.current) await mutate(() => pjDeleteStage(stage.id))
  }
  const move = (id, direction) => mutate(async () => {
    for (const update of stageOrderUpdates(stages, id, direction)) {
      await pjUpdateStage(update.id, { ordem: update.ordem })
    }
  })
  const keyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
    if (event.key !== 'Tab') return
    const controls = [...root.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')]
    const first = controls[0], last = controls[controls.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root.current)) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }
  return <div className="psm-overlay" onMouseDown={event => { if (event.target === event.currentTarget) close() }}>
    <section className="psm-dialog" ref={root} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="project-stages-title" onKeyDown={keyDown}>
      <header><div><h2 id="project-stages-title">Configurar etapas</h2><p>{project.nome || project.titulo}</p></div><button type="button" aria-label="Fechar configuração de etapas" disabled={busy} onClick={close}><Icon n="x" s={18}/></button></header>
      <div className="psm-body" aria-busy={busy}>
        {error && <p className="psm-error" role="alert">{error}</p>}
        {!stages ? error ? <button type="button" className="psm-secondary" disabled={busy} onClick={() => { setError(''); load().catch(e => setError(e.message)) }}>Tentar novamente</button> : <p role="status">Carregando etapas…</p> : <>
          <p className="psm-hint">Organize as colunas do Kanban. Cada alteração é salva ao confirmar.</p>
          <ol className="psm-stages">{stages.map((stage, index) => <li key={stage.id}>
            <span className="psm-number">{index + 1}</span>
            {editing === stage.id ? <form className="psm-edit" onSubmit={event => { event.preventDefault(); if (name.trim()) mutate(() => pjUpdateStage(stage.id, { nome: name.trim() })) }}>
              <input autoFocus aria-label="Nome da etapa" maxLength={255} value={name} disabled={busy} onChange={event => setName(event.target.value)}/>
              <button type="submit" disabled={busy || !name.trim()} aria-label="Salvar nome da etapa"><Icon n="check" s={16}/></button>
              <button type="button" disabled={busy} aria-label="Cancelar edição da etapa" onClick={() => setEditing(null)}><Icon n="x" s={16}/></button>
            </form> : <>
              <div className="psm-stage-name"><button type="button" title="Renomear etapa" disabled={busy || !!editing} onClick={() => { setEditing(stage.id); setName(stage.nome) }}>{stage.nome}</button><small>{stage.tasks?.length || 0} tarefas{stage.is_ganho ? ' · Conclusão' : ''}</small></div>
              <div className="psm-actions">
                <button type="button" disabled={busy || !!editing || index === 0} aria-label={`Mover ${stage.nome} para cima`} onClick={() => move(stage.id, -1)}><Icon n="arrow_up" s={15}/></button>
                <button type="button" disabled={busy || !!editing || index === stages.length - 1} aria-label={`Mover ${stage.nome} para baixo`} onClick={() => move(stage.id, 1)}><Icon n="arrow_down" s={15}/></button>
                <button type="button" className="psm-delete" disabled={busy || !!editing} aria-label={`Excluir etapa ${stage.nome}`} onClick={() => remove(stage)}><Icon n="trash" s={15}/></button>
              </div>
            </>}
          </li>)}</ol>
          {!stages.length && <p>Nenhuma etapa cadastrada.</p>}
          <form className="psm-add" onSubmit={event => { event.preventDefault(); if (newName.trim()) mutate(() => pjCreateStage(project.id, { nome: newName.trim(), ordem: Math.max(-1, ...stages.map(stage => Number(stage.ordem) || 0)) + 1 })) }}>
            <input aria-label="Nome da nova etapa" placeholder="Nome da nova etapa" maxLength={255} value={newName} disabled={busy || !!editing} onChange={event => setNewName(event.target.value)}/>
            <button type="submit" className="psm-primary" disabled={busy || !!editing || !newName.trim()}><Icon n="plus" s={14}/> Adicionar etapa</button>
          </form>
        </>}
      </div>
      <footer><span role="status">{busy ? 'Salvando…' : ''}</span><button type="button" className="psm-secondary" disabled={busy} onClick={close}>Fechar</button></footer>
    </section>
  </div>
}
